import { Button } from '@shared/components/ui/button';
import { ModalCenter, type ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePaymentMethods, useUpdateOrder } from '../../hooks';
import type { OrderLine } from '../../models/Order';

interface AdditionalInfoModalProps {
  paymentMethodId?: number;
  orderId?: number;
  lines?: OrderLine[];
  onUpdate?: () => void;
}

export { type ModalCenterRef };

export const AdditionalInfoModal = forwardRef<ModalCenterRef, AdditionalInfoModalProps>(
  ({ paymentMethodId, orderId, lines = [], onUpdate }, ref) => {
    const { t } = useTranslation('viewOrdersModule');
    const { paymentMethods, loading: paymentMethodsLoading } = usePaymentMethods();
    const { updateOrder, loading: isUpdating } = useUpdateOrder();

    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');

    useEffect(() => {
      if (paymentMethodId) {
        setSelectedPaymentMethod(String(paymentMethodId));
      }
    }, [paymentMethodId]);

    const handleSave = async () => {
      if (!orderId || !selectedPaymentMethod) return;

      const linesPayload = lines.map((line) => {
        const linePayload: any = {
          product_id: line.product_id,
          quantity: line.qty, // API espera 'quantity', no 'qty'
          amount: line.amount,
        };

        // Solo agregar parámetros si tienen valores
        if (line.external_product_id) {
          linePayload.external_product_id = line.external_product_id;
        }
        if (line.external_variant_id) {
          linePayload.external_variant_id = line.external_variant_id;
        }
        if (line.pack_id) {
          linePayload.pack_id = line.pack_id;
        }

        return linePayload;
      });

      const payload = {
        payment_method_id: Number(selectedPaymentMethod),
        lines: linesPayload,
      };

      const success = await updateOrder(orderId, payload);

      if (success) {
        onUpdate?.();
        handleClose();
      }
    };

    const handleClose = () => {
      if (ref && 'current' in ref && ref.current) {
        ref.current.closeModal();
      }
    };

    return (
      <ModalCenter
        ref={ref}
        title={t('modals.additional-info.title')}
        icon={<FontAwesomeIcon icon={faXmark} className="hidden" />}
        type="default"
        width="lg"
        fullScreenMobile={true}
      >
        <div className="flex flex-col h-full">
          {/* Contenido principal */}
          <div className="flex-1 py-4 pb-24 md:pb-4">
            <label className="text-sm font-medium">{t('modals.additional-info.payment-method')}</label>
            <Select
              value={selectedPaymentMethod}
              onValueChange={setSelectedPaymentMethod}
              disabled={paymentMethodsLoading}
            >
              <SelectTrigger className="w-full mt-1">
                <SelectValue placeholder={t('modals.additional-info.select-payment')} />
              </SelectTrigger>
              <SelectContent>
                {paymentMethods.map((method) => (
                  <SelectItem key={method.id} value={String(method.id)}>
                    {method.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Botones de acción - Fijos en mobile */}
          <div className="fixed bottom-0 left-0 right-0 p-4 md:pb-0 md:px-0 md:relative md:border-t-0">
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="lg" onClick={handleClose}>
                {t('modals.additional-info.cancel')}
              </Button>
              <Button variant="primary" size="lg" onClick={handleSave} disabled={isUpdating || !selectedPaymentMethod}>
                {isUpdating ? t('modals.shipping-data.saving') : t('modals.additional-info.save')}
              </Button>
            </div>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

AdditionalInfoModal.displayName = 'AdditionalInfoModal';
